import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Download, ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx';

interface AssignmentData {
  dealId?: string;
  assignorName: string;
  assignorAddress: string;
  assigneeName: string;
  assigneeAddress: string;
  recordedDocumentType: string;
  landContractDate: string;
  sellerName: string;
  buyerName: string;
  referenceType: string;
  landContractLiber: string;
  landContractPage: string;
  landContractDocumentNumber: string;
  recordingDate: string;
  county: string;
  municipalityType: string;
  township: string;
  legalDescription: string;
  commonAddress: string;
  balanceDue: string;
  interestDate: string;
  assignmentDate: string;
  notaryName: string;
  notaryCounty: string;
  notaryExpiration: string;
  actingCounty: string;
  draftedByName: string;
  draftedByAddress: string;
}

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

export function AssignmentGenerator() {
  const { toast } = useToast();
  const [deals, setDeals] = useState<any[]>([]);
  const [formData, setFormData] = useState<AssignmentData>(() => {
    const saved = localStorage.getItem('assignmentProgress');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error loading saved form data:', e);
      }
    }
    return {
      assignorName: "",
      assignorAddress: "",
      assigneeName: "",
      assigneeAddress: "",
      recordedDocumentType: "Land Contract",
      landContractDate: "",
      sellerName: "",
      buyerName: "",
      referenceType: "liber-page",
      landContractLiber: "",
      landContractPage: "",
      landContractDocumentNumber: "",
      recordingDate: "",
      county: "",
      municipalityType: "Township",
      township: "",
      legalDescription: "",
      commonAddress: "",
      balanceDue: "",
      interestDate: new Date().toISOString().split('T')[0],
      assignmentDate: new Date().toISOString().split('T')[0],
      notaryName: "",
      notaryCounty: "",
      notaryExpiration: "",
      actingCounty: "",
      draftedByName: "First National Bank of America",
      draftedByAddress: "P.O. Box 300, East Lansing, MI 48826",
    };
  });
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);
  const [extractedPdfText, setExtractedPdfText] = useState<string>("");
  const [parseText, setParseText] = useState('');
  const [isParseOpen, setIsParseOpen] = useState(false);
  const [legalDescImageData, setLegalDescImageData] = useState<string | null>(null);
  const [isExtractingLegalDescImage, setIsExtractingLegalDescImage] = useState(false);

  useEffect(() => {
    loadDeals();
  }, []);

  useEffect(() => {
    localStorage.setItem('assignmentProgress', JSON.stringify(formData));
  }, [formData]);

  const loadDeals = async () => {
    const { data, error } = await supabase
      .from('deals')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error loading deals:', error);
      return;
    }
    setDeals(data || []);
  };

  const loadDealData = async (dealId: string) => {
    const deal = deals.find(d => d.id === dealId);
    if (!deal) return;

    setFormData({
      ...formData,
      dealId,
      assignorName: deal.seller_name || "",
      assignorAddress: deal.seller_address || "",
      sellerName: deal.seller_name || "",
      buyerName: deal.buyer_name || "",
      commonAddress: deal.property_address || "",
      balanceDue: deal.current_balance?.toString() || "",
    });
  };

  const handlePdfUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || file.type !== 'application/pdf') {
      toast({
        title: "Invalid file",
        description: "Please upload a PDF file",
        variant: "destructive",
      });
      return;
    }

    setIsProcessingPdf(true);
    toast({
      title: "Processing PDF",
      description: "Extracting text from document...",
    });
    
    try {
      // First, try client-side extraction with pdfjs-dist
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url
      ).toString();

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let fullText = "";
      
      // Extract text from all pages
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => item.str)
          .filter((text: string) => text.trim().length > 0)
          .join(' ');
        
        if (pageText.trim()) {
          fullText += pageText + "\n\n";
        }
      }

      console.log('Client-side PDF extraction - text length:', fullText.length);
      console.log('First 500 chars:', fullText.substring(0, 500));

      // Store the full text for manual copying
      setExtractedPdfText(fullText);
      
      // Check if we got meaningful text
      if (!fullText || fullText.trim().length < 50) {
        toast({
          title: "Limited text extracted",
          description: "PDF may be image-based. Try using OCR software to convert it first, then paste the text directly into the parser.",
          variant: "destructive",
        });
        setExtractedPdfText(""); // Clear empty text
        return;
      }
      
      // Try to auto-extract fields with improved patterns
      
      // Legal description - starts with "Land situated" or similar, ends before "Commonly known"
      const legalDescMatch = fullText.match(/Land situated in[^]*?(?=Commonly known|Parcel ID|This property may)/i) ||
                            fullText.match(/LEGAL DESCRIPTION[:\s]+([\s\S]*?)(?=COMMONLY KNOWN|COMMON ADDRESS|PARCEL|$)/i);
      
      // Common address - after "Commonly known as:"
      const addressMatch = fullText.match(/Commonly known as:\s*([^\n]+)/i) ||
                          fullText.match(/(?:COMMON ADDRESS|PROPERTY ADDRESS)[:\s]+([^\n]+)/i);
      
      // Parcel number
      const parcelMatch = fullText.match(/Parcel ID No[.\s:]+([0-9\-]+)/i) ||
                         fullText.match(/PARCEL[:\s#]*([0-9\-]+)/i);
      
      // Document recording info - look for Liber and Page in recorded plat references
      const liberMatch = fullText.match(/Liber\s+(\d+)\s+of\s+Deeds/i) ||
                        fullText.match(/LIBER[:\s]*(\d+)/i);
      const pageMatch = fullText.match(/Page\s+(\d+)[,\s]+[A-Za-z\s]+County/i) ||
                       fullText.match(/PAGE[:\s]*(\d+)/i);
      
      // Direct document number if present
      const docNumberMatch = fullText.match(/(?:DOCUMENT|DOC|INSTRUMENT)\s*(?:NUMBER|NO\.?|#)?[:\s]*([A-Z0-9\-]+)/i);
      
      // Extract county and township from legal description
      const countyMatch = fullText.match(/County of\s+([A-Za-z\s]+),\s+State/i) ||
                         fullText.match(/([A-Za-z]+)\s+County/i);
      const townshipMatch = fullText.match(/(?:City|Township|Village)\s+of\s+([A-Za-z\s]+)/i);
      
      // Extract seller and buyer names
      const sellerMatch = fullText.match(/referred to as the "Seller,?" whose address is[^,]*, and ([^,]+),/i) ||
                         fullText.match(/between\s+([^,]+),\s+hereinafter referred to as the "Seller/i);
      const buyerMatch = fullText.match(/referred to as the "Purchaser",? whose address is[^,]*, and ([^,]+)/i) ||
                        fullText.match(/and\s+([^,]+),\s+hereinafter referred to as the "Purchaser/i);
      
      const updates: Partial<AssignmentData> = {};
      
      if (legalDescMatch) {
        updates.legalDescription = legalDescMatch[0]?.trim() || legalDescMatch[1]?.trim() || '';
      }
      
      if (addressMatch) {
        updates.commonAddress = addressMatch[1].trim();
      }
      
      if (parcelMatch) {
        // Store parcel number in notes or create a new field if needed
        const parcelNote = `Parcel ID: ${parcelMatch[1].trim()}`;
        console.log('Extracted Parcel:', parcelNote);
      }
      
      if (docNumberMatch) {
        updates.landContractDocumentNumber = docNumberMatch[1].trim();
      }
      
      if (liberMatch) {
        updates.landContractLiber = liberMatch[1].trim();
      }
      
      if (pageMatch) {
        updates.landContractPage = pageMatch[1].trim();
      }
      
      if (countyMatch) {
        updates.county = countyMatch[1].trim();
      }
      
      if (townshipMatch) {
        updates.township = townshipMatch[1].trim();
      }
      
      if (sellerMatch) {
        const sellerName = sellerMatch[1]?.trim();
        if (sellerName) {
          updates.sellerName = sellerName;
          updates.assignorName = sellerName; // Pre-fill assignor as seller
        }
      }
      
      if (buyerMatch) {
        const buyerName = buyerMatch[1]?.trim();
        if (buyerName) {
          updates.buyerName = buyerName;
        }
      }
      
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({
          ...prev,
          ...updates
        }));
        
        const extractedFields = Object.keys(updates).join(', ');
        toast({
          title: "PDF data extracted",
          description: `Auto-filled: ${extractedFields}`,
        });
      } else {
        toast({
          title: "PDF processed",
          description: "No fields could be auto-extracted. You can copy from the extracted text below.",
        });
      }
    } catch (error) {
      console.error('PDF processing error:', error);
      toast({
        title: "Error processing PDF",
        description: "Could not parse PDF. Please copy text manually.",
        variant: "destructive",
      });
    } finally {
      setIsProcessingPdf(false);
      event.target.value = '';
    }
  };

  const clearForm = () => {
    const emptyForm: AssignmentData = {
      assignorName: "",
      assignorAddress: "",
      assigneeName: "",
      assigneeAddress: "",
      recordedDocumentType: "Land Contract",
      landContractDate: "",
      sellerName: "",
      buyerName: "",
      referenceType: "liber-page",
      landContractLiber: "",
      landContractPage: "",
      landContractDocumentNumber: "",
      recordingDate: "",
      county: "",
      municipalityType: "Township",
      township: "",
      legalDescription: "",
      commonAddress: "",
      balanceDue: "",
      interestDate: new Date().toISOString().split('T')[0],
      assignmentDate: new Date().toISOString().split('T')[0],
      notaryName: "",
      notaryCounty: "",
      notaryExpiration: "",
      actingCounty: "",
      draftedByName: "First National Bank of America",
      draftedByAddress: "P.O. Box 300, East Lansing, MI 48826",
    };
    setFormData(emptyForm);
    localStorage.removeItem('assignmentProgress');
    toast({
      title: "Form Cleared",
      description: "All fields have been reset",
    });
  };
  const extractImageDataUrlFromClipboard = async (clipboardData?: DataTransfer | null) => {
    const items = clipboardData?.items;
    if (!items) return null;

    for (const item of items) {
      if (item.type?.startsWith('image/')) {
        const file = item.getAsFile();
        if (!file) continue;

        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        return base64;
      }
    }
    return null;
  };

  const pasteLegalDescScreenshotFromClipboard = async () => {
    try {
      const items = await (navigator.clipboard as any)?.read?.();
      if (!items || items.length === 0) {
        toast({
          title: "Clipboard blocked",
          description: "Browser blocked clipboard access. Click the paste box and press Ctrl+V / Cmd+V instead.",
          variant: "destructive",
        });
        return;
      }

      for (const item of items) {
        const type = item.types?.find((t: string) => t.startsWith('image/'));
        if (!type) continue;

        const blob: Blob = await item.getType(type);
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        setLegalDescImageData(base64);
        toast({ title: "Screenshot captured", description: "Click Extract from Image to process." });
        return;
      }

      toast({
        title: "No image found",
        description: "Your clipboard doesn't contain an image. If you're copying selectable text, paste into the text box below.",
        variant: "destructive",
      });
    } catch (err: any) {
      toast({
        title: "Clipboard access denied",
        description: err?.message || "Click the paste box and press Ctrl+V / Cmd+V.",
        variant: "destructive",
      });
    }
  };

  const extractLegalDescriptionFromImage = async () => {
    if (!legalDescImageData) {
      toast({
        title: "No screenshot",
        description: "Paste a screenshot first.",
        variant: "destructive",
      });
      return;
    }

    setIsExtractingLegalDescImage(true);
    try {
      const { data, error } = await supabase.functions.invoke("extract-legal-description-screenshot", {
        body: { imageData: legalDescImageData },
      });

      if (error) {
        // Surface status-ish info when available
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || "Extraction failed");
      }

      const extracted = String(data.legal_description || '').trim();
      if (!extracted) {
        toast({
          title: "No legal description found",
          description: "Try a tighter screenshot of just the LEGAL DESCRIPTION section.",
          variant: "destructive",
        });
        return;
      }

      setFormData((prev) => ({ ...prev, legalDescription: extracted }));
      setLegalDescImageData(null);
      toast({ title: "Extracted", description: "Legal description populated." });
      setIsParseOpen(false);
    } catch (e: any) {
      const msg = e?.message || "Failed to extract from image";
      toast({ title: "AI extraction failed", description: msg, variant: "destructive" });
    } finally {
      setIsExtractingLegalDescImage(false);
    }
  };

  const extractLegalDescription = () => {
    if (!parseText.trim()) {
      toast({
        title: "No text to parse",
        description: "Please paste some text first",
        variant: "destructive",
      });
      return;
    }

    const patterns = [
      /(?:described as|legal description|legally described as)[:\s]+(.+?)(?:commonly known|parcel|tax|$)/is,
      /(?:SITUATED IN|Located in|being in)[:\s]+(.+?)(?:commonly known|parcel|tax|$)/is,
      /((?:Section|Sec\.|Township|Twp\.|Range|Rng\.)[\s\S]+?)(?:commonly known|parcel|tax|also known|$)/is,
      /(Lot[s]?\s+\d+[\s\S]+?(?:Subdivision|Addition|Plat)[\s\S]+?)(?:commonly known|parcel|tax|also known|$)/is,
      /((?:.*(?:situated|located|being|township|range|section|lot|block|subdivision|metes|bounds|chains|rods|degrees).*)+)/is
    ];

    let extracted = '';
    for (const pattern of patterns) {
      const match = parseText.match(pattern);
      if (match && match[1] && match[1].trim().length > 20) {
        extracted = match[1].trim();
        break;
      }
    }

    if (extracted) {
      extracted = extracted.replace(/\s+/g, ' ').replace(/\n\s*\n/g, '\n').trim();
      setFormData(prev => ({ ...prev, legalDescription: extracted }));
      toast({
        title: "Success!",
        description: "Legal description extracted and populated",
      });
      setIsParseOpen(false);
    } else {
      toast({
        title: "Could not find legal description",
        description: "Please try copying a larger section or enter manually.",
        variant: "destructive",
      });
    }
  };

  const generateDocument = async () => {
    if (!formData.assignorName || !formData.assigneeName || !formData.legalDescription) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      const doc = new Document({
        sections: [{
          properties: {
            page: {
              margin: {
                top: 1440,  // 1 inch
                bottom: 1440,
                left: 1440,
                right: 1440
              }
            }
          },
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: "Assignment of Seller's Interest in Land Contract",
                  bold: false,
                  color: "000000"
                })
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `For a valuable consideration, receipt of which is acknowledged, the undersigned person(s), whose address is ${formData.assignorAddress}, hereby assign(s) and set(s) over to ${formData.assigneeName}, the assignee, whose address is ${formData.assigneeAddress}, a certain ${formData.recordedDocumentType} dated ${formData.landContractDate}, executed by and between ${formData.sellerName}, as Seller, and ${formData.buyerName}, as Buyer, as evidenced by ${formData.recordedDocumentType === "Memorandum of Land Contract" ? "Memorandum of Land Contract" : "Land Contract"} recorded on ${formData.recordingDate}, ${formData.referenceType === 'liber-page' ? `in Liber ${formData.landContractLiber}, Page ${formData.landContractPage}` : `as Document Number ${formData.landContractDocumentNumber}`}, in the Register of Deeds Office of ${formData.county} County, Michigan, for the land and property situated in the ${formData.municipalityType} of ${formData.township}, County of ${formData.county} and State of Michigan, described as:`,
                  color: "000000"
                })
              ],
              spacing: { after: 200 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `SITUATED IN THE ${formData.municipalityType.toUpperCase()} OF ${formData.township.toUpperCase()}, COUNTY OF ${formData.county.toUpperCase()}, STATE OF MICHIGAN`,
                  bold: false,
                  color: "000000"
                })
              ],
              alignment: AlignmentType.CENTER,
              spacing: { before: 200, after: 200 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: formData.legalDescription,
                  color: "000000"
                })
              ],
              spacing: { after: 200 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `Commonly known as ${formData.commonAddress}.`,
                  color: "000000"
                })
              ],
              spacing: { after: 200 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `together with all sums due and to become due on the Land Contract, and covenants that there is now owing on the Land Contract $${parseFloat(formData.balanceDue || "0").toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} with interest from ${new Date(formData.interestDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.`,
                  color: "000000"
                })
              ],
              spacing: { after: 200 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `Dated this ${new Date(formData.assignmentDate).getDate()} day of ${new Date(formData.assignmentDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
                  color: "000000"
                })
              ],
              spacing: { after: 200 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Signed by:",
                  color: "000000"
                })
              ],
              spacing: { after: 100 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "",
                  color: "000000"
                })
              ],
              spacing: { before: 200, after: 10 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "_______________________________",
                  color: "000000"
                })
              ],
              spacing: { after: 10 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: formData.assignorName,
                  color: "000000"
                })
              ],
              spacing: { after: 400 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "STATE OF MICHIGAN",
                  color: "000000"
                }),
                new TextRun({
                  text: "\t\t\t)",
                  color: "000000"
                })
              ],
              spacing: { before: 400, after: 10 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `COUNTY OF ${formData.notaryCounty.toUpperCase()}`,
                  color: "000000"
                }),
                new TextRun({
                  text: "\t\t\tss.",
                  color: "000000"
                })
              ],
              spacing: { after: 200 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `The foregoing instrument was acknowledged before this ${new Date(formData.assignmentDate).getDate()}${getOrdinalSuffix(new Date(formData.assignmentDate).getDate())} day of ${new Date(formData.assignmentDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}, by ${formData.assignorName}.`,
                  color: "000000"
                })
              ],
              spacing: { after: 400 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "\t\t\t\t\t\t_______________________________",
                  color: "000000"
                })
              ],
              spacing: { after: 10 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "\t\t\t\t\t\t" + formData.notaryName.toUpperCase(),
                  color: "000000"
                })
              ],
              spacing: { after: 10 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "\t\t\t\t\t\tNOTARY PUBLIC, STATE OF MICHIGAN",
                  color: "000000"
                })
              ],
              spacing: { after: 10 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "\t\t\t\t\t\tCOUNTY OF " + formData.notaryCounty.toUpperCase(),
                  color: "000000"
                })
              ],
              spacing: { after: 10 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "\t\t\t\t\t\tMy Commission Expires " + formData.notaryExpiration,
                  color: "000000"
                })
              ],
              spacing: { after: 10 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `Acting in the County of: ${formData.actingCounty}, State of Michigan`,
                  color: "000000"
                })
              ],
              spacing: { after: 10 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `My Commission Expires ${formData.notaryExpiration}`,
                  color: "000000"
                })
              ],
              spacing: { after: 400 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Drafted by and after recording return to:",
                  color: "000000"
                })
              ],
              spacing: { after: 10 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `${formData.draftedByName || "First National Bank of America"}`,
                  color: "000000"
                })
              ],
              spacing: { after: 10 }
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `${formData.draftedByAddress || "P.O. Box 300, East Lansing, MI 48826"}`,
                  color: "000000"
                })
              ],
              spacing: { after: 200 }
            }),
          ]
        }]
      });

      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Assignment_${formData.assigneeName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
      link.click();
      window.URL.revokeObjectURL(url);

      localStorage.removeItem('assignmentProgress');
      toast({
        title: "Success",
        description: "Assignment of Land Contract generated successfully. Form progress cleared."
      });
    } catch (error) {
      console.error('Error generating document:', error);
      toast({
        title: "Error",
        description: "Failed to generate document",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Assignment of Land Contract Generator</span>
          <Button variant="outline" size="sm" onClick={clearForm}>
            Clear Form
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Load from Deal (Optional)</Label>
          <Select onValueChange={loadDealData}>
            <SelectTrigger>
              <SelectValue placeholder="Select a deal..." />
            </SelectTrigger>
            <SelectContent>
              {deals.map(deal => (
                <SelectItem key={deal.id} value={deal.id}>
                  {deal.deal_title} - {deal.buyer_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Upload Land Contract / Memorandum (Optional)</Label>
          <Input
            type="file"
            accept=".pdf"
            onChange={handlePdfUpload}
            disabled={isProcessingPdf}
          />
          {isProcessingPdf && (
            <p className="text-sm text-muted-foreground">Processing document...</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Assignor Name (Seller) *</Label>
            <Input
              value={formData.assignorName}
              onChange={(e) => setFormData({ ...formData, assignorName: e.target.value })}
              placeholder="John R. Murray and Shelly J. Murray"
            />
          </div>

          <div className="space-y-2">
            <Label>Assignor Address</Label>
            <Input
              value={formData.assignorAddress}
              onChange={(e) => setFormData({ ...formData, assignorAddress: e.target.value })}
              placeholder="11725 Gates Road, Mulliken, MI 48861"
            />
          </div>

          <div className="space-y-2">
            <Label>Assignee Name (Investor) *</Label>
            <Input
              value={formData.assigneeName}
              onChange={(e) => setFormData({ ...formData, assigneeName: e.target.value })}
              placeholder="First National Bank of America"
            />
          </div>

          <div className="space-y-2">
            <Label>Assignee Address</Label>
            <Input
              value={formData.assigneeAddress}
              onChange={(e) => setFormData({ ...formData, assigneeAddress: e.target.value })}
              placeholder="241 East Saginaw, PO Box 980, East Lansing, MI 48826"
            />
          </div>

          <div className="space-y-2">
            <Label>Original Seller Name</Label>
            <Input
              value={formData.sellerName}
              onChange={(e) => setFormData({ ...formData, sellerName: e.target.value })}
              placeholder="John R. Murray and Shelly J. Murray"
            />
          </div>

          <div className="space-y-2">
            <Label>Original Buyer Name</Label>
            <Input
              value={formData.buyerName}
              onChange={(e) => setFormData({ ...formData, buyerName: e.target.value })}
              placeholder="James A. Nelson"
            />
          </div>

          <div className="space-y-2">
            <Label>Recorded Document Type</Label>
            <Select value={formData.recordedDocumentType} onValueChange={(value) => setFormData({ ...formData, recordedDocumentType: value })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Land Contract">Land Contract</SelectItem>
                <SelectItem value="Memorandum of Land Contract">Memorandum of Land Contract</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{formData.recordedDocumentType} Date</Label>
            <Input
              type="date"
              value={formData.landContractDate}
              onChange={(e) => setFormData({ ...formData, landContractDate: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Recording Date</Label>
            <Input
              type="date"
              value={formData.recordingDate}
              onChange={(e) => setFormData({ ...formData, recordingDate: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Recording Reference Type</Label>
            <Select
              value={formData.referenceType}
              onValueChange={(value) => setFormData({ ...formData, referenceType: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="liber-page">Liber and Page</SelectItem>
                <SelectItem value="document-number">Document Number</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.referenceType === 'liber-page' ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{formData.recordedDocumentType} Liber</Label>
                <Input
                  value={formData.landContractLiber}
                  onChange={(e) => setFormData({ ...formData, landContractLiber: e.target.value })}
                  placeholder="3126"
                />
              </div>
              <div className="space-y-2">
                <Label>{formData.recordedDocumentType} Page</Label>
                <Input
                  value={formData.landContractPage}
                  onChange={(e) => setFormData({ ...formData, landContractPage: e.target.value })}
                  placeholder="0594"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Document Number</Label>
              <Input
                value={formData.landContractDocumentNumber}
                onChange={(e) => setFormData({ ...formData, landContractDocumentNumber: e.target.value })}
                placeholder="e.g., 2024-123456"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Municipality Type</Label>
            <Select value={formData.municipalityType} onValueChange={(value) => setFormData({ ...formData, municipalityType: value })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Township">Township</SelectItem>
                <SelectItem value="City">City</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{formData.municipalityType} Name *</Label>
            <Input
              value={formData.township}
              onChange={(e) => setFormData({ ...formData, township: e.target.value })}
              placeholder="Oneida"
            />
          </div>

          <div className="space-y-2">
            <Label>County *</Label>
            <Input
              value={formData.county}
              onChange={(e) => setFormData({ ...formData, county: e.target.value })}
              placeholder="Eaton"
            />
          </div>

          <div className="space-y-2">
            <Label>Common Address</Label>
            <Input
              value={formData.commonAddress}
              onChange={(e) => setFormData({ ...formData, commonAddress: e.target.value })}
              placeholder="1070 E St Joe Hwy, Grand Ledge, MI 48837"
            />
          </div>

          <div className="space-y-2">
            <Label>Balance Due</Label>
            <Input
              type="number"
              value={formData.balanceDue}
              onChange={(e) => setFormData({ ...formData, balanceDue: e.target.value })}
              placeholder="115445.96"
            />
          </div>

          <div className="space-y-2">
            <Label>Interest Calculated From</Label>
            <Input
              type="date"
              value={formData.interestDate}
              onChange={(e) => setFormData({ ...formData, interestDate: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Notary Name</Label>
            <Input
              value={formData.notaryName}
              onChange={(e) => setFormData({ ...formData, notaryName: e.target.value })}
              placeholder="Annie K. Barr"
            />
          </div>

          <div className="space-y-2">
            <Label>Notary County</Label>
            <Input
              value={formData.notaryCounty}
              onChange={(e) => setFormData({ ...formData, notaryCounty: e.target.value })}
              placeholder="Clinton"
            />
          </div>

          <div className="space-y-2">
            <Label>Notary Expiration</Label>
            <Input
              value={formData.notaryExpiration}
              onChange={(e) => setFormData({ ...formData, notaryExpiration: e.target.value })}
              placeholder="July 9, 2026"
            />
          </div>
        </div>

        {extractedPdfText && (
          <div className="space-y-2 p-4 border rounded-lg bg-muted/50">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Extracted PDF Text (Copy From Here)</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(extractedPdfText);
                  toast({
                    title: "Copied to clipboard",
                    description: "PDF text copied successfully",
                  });
                }}
              >
                Copy All Text
              </Button>
            </div>
            <Textarea
              value={extractedPdfText}
              readOnly
              className="font-mono text-xs"
              rows={10}
            />
            <p className="text-xs text-muted-foreground">
              Select and copy the legal description or any other information from the text above
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label>Legal Description *</Label>
          
          <Collapsible open={isParseOpen} onOpenChange={setIsParseOpen} className="mb-3">
            <CollapsibleTrigger asChild>
              <Button type="button" variant="outline" size="sm" className="w-full justify-between mb-2">
                Parse Legal Description from Text
                <ChevronDown className={`h-4 w-4 transition-transform ${isParseOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 pt-2">
              <p className="text-sm text-muted-foreground">
                Paste text from any document below (Ctrl+V / Cmd+V), or use the button to paste from clipboard.
              </p>

              {/* Screenshot/PDF-image workflow (when clipboard has no selectable text) */}
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  If you copied from a PDF and paste won’t work, it’s usually because it’s an image. Paste a screenshot here and we’ll extract it.
                </p>
                <div
                  className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer transition-colors hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  tabIndex={0}
                  contentEditable
                  suppressContentEditableWarning
                  onPaste={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const base64 = await extractImageDataUrlFromClipboard(e.clipboardData);
                    if (!base64) {
                      toast({
                        title: "No image found",
                        description: "Clipboard didn’t include an image. If you have selectable text, paste into the text box below.",
                        variant: "destructive",
                      });
                      return;
                    }
                    setLegalDescImageData(base64);
                    toast({ title: "Screenshot captured", description: "Click Extract from Image to process." });
                  }}
                  onClick={(e) => (e.currentTarget as HTMLDivElement).focus()}
                >
                  {!legalDescImageData ? (
                    <div className="space-y-2">
                      <p className="text-muted-foreground">
                        Click here and press{' '}
                        <kbd className="px-2 py-1 bg-muted rounded text-xs">Ctrl+V</kbd> to paste a screenshot
                      </p>
                      <div className="flex items-center justify-center gap-2 pt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            pasteLegalDescScreenshotFromClipboard();
                          }}
                        >
                          Paste from Clipboard
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <img
                        src={legalDescImageData}
                        alt="Pasted legal description screenshot"
                        className="max-h-48 mx-auto rounded border"
                      />
                      <div className="flex gap-2 justify-center">
                        <Button
                          type="button"
                          size="sm"
                          onClick={extractLegalDescriptionFromImage}
                          disabled={isExtractingLegalDescImage}
                        >
                          {isExtractingLegalDescImage ? "Extracting..." : "Extract from Image (AI)"}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setLegalDescImageData(null)}
                          disabled={isExtractingLegalDescImage}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 mb-2">
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      // Try reading text directly first
                      const text = await navigator.clipboard.readText();
                      if (text && text.trim().length > 0) {
                        const cleanedText = text
                          .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width characters
                          .replace(/\u00A0/g, ' ') // Replace non-breaking spaces
                          .trim();
                        setParseText(cleanedText);
                        toast({
                          title: "Text pasted from clipboard",
                          description: `${cleanedText.length} characters loaded`,
                        });
                      } else {
                        toast({
                          title: "No text in clipboard",
                          description: "Please copy text first, then try again. If you copied an image, paste it into the screenshot box above.",
                          variant: "destructive",
                        });
                      }
                    } catch (err) {
                      console.error('Clipboard read error:', err);
                      toast({
                        title: "Clipboard access denied",
                        description: "Please click inside the text area below and use Ctrl+V to paste",
                        variant: "destructive",
                      });
                    }
                  }}
                  variant="outline"
                  size="sm"
                >
                  Paste text from Clipboard
                </Button>
              </div>
              <Textarea
                placeholder="Click here first, then paste your text (Ctrl+V or Cmd+V)..."
                value={parseText}
                onChange={(e) => setParseText(e.target.value)}
                onPaste={(e) => {
                  const plain = e.clipboardData.getData('text/plain');
                  const html = e.clipboardData.getData('text/html');

                  const htmlToText = (input: string) =>
                    input
                      .replace(/<\/?(script|style)[^>]*>/gi, '')
                      .replace(/<br\s*\/?\s*>/gi, '\n')
                      .replace(/<\/?p[^>]*>/gi, '\n')
                      .replace(/<[^>]+>/g, ' ')
                      .replace(/&nbsp;/g, ' ');

                  const pastedText = (plain && plain.trim().length > 0)
                    ? plain
                    : (html && html.trim().length > 0)
                      ? htmlToText(html)
                      : '';

                  const cleanedText = pastedText
                    .replace(/\r\n/g, '\n')
                    .replace(/[\u200B-\u200D\uFEFF]/g, '')
                    .replace(/\u00A0/g, ' ')
                    .trim();

                  if (cleanedText.length === 0) {
                    // Don’t block default paste if there’s no text in the clipboard.
                    toast({
                      title: "Nothing to paste",
                      description: "Your clipboard doesn’t contain selectable text (it may be an image/PDF screenshot). Try copying from a text-selectable PDF, or run OCR first.",
                      variant: "destructive",
                    });
                    return;
                  }

                  e.preventDefault();
                  setParseText(cleanedText);
                  toast({
                    title: "Text pasted successfully",
                    description: `${cleanedText.length} characters loaded`,
                  });
                }}
                rows={8}
                className="font-mono text-xs"
                autoFocus={false}
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={extractLegalDescription}
                  size="sm"
                  className="flex-1"
                >
                  Extract Legal Description
                </Button>
                <Button
                  type="button"
                  onClick={() => setParseText('')}
                  variant="outline"
                  size="sm"
                >
                  Clear
                </Button>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <Textarea
            value={formData.legalDescription}
            onChange={(e) => setFormData({ ...formData, legalDescription: e.target.value })}
            placeholder="Paste or type the legal description here..."
            rows={6}
          />
        </div>

        <Button onClick={generateDocument} className="w-full">
          <Download className="mr-2 h-4 w-4" />
          Generate Assignment of Land Contract
        </Button>
      </CardContent>
    </Card>
  );
}
